import React, { useRef, useState, useEffect } from "react";

/**
 * SyncedScrollTable - A wrapper that adds synchronized top + bottom horizontal scrollbars
 * 
 * Usage:
 * <SyncedScrollTable maxHeight="calc(100vh - 300px)">
 *   <table>...</table>
 * </SyncedScrollTable>
 */
export function SyncedScrollTable({ 
  children, 
  maxHeight = "calc(100vh - 280px)",
  className = "",
  showFadeHint = true 
}) {
  const topScrollRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [showRightFade, setShowRightFade] = useState(false);
  const [showLeftFade, setShowLeftFade] = useState(false);

  // Sync scroll between top scrollbar and table
  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleTableScroll = () => {
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
      updateFadeIndicators();
    }
  };

  // Update fade indicators based on scroll position
  const updateFadeIndicators = () => {
    if (!tableContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
    const maxScroll = scrollWidth - clientWidth;
    
    // Show right fade if there's more content to scroll
    setShowRightFade(scrollLeft < maxScroll - 5);
    // Show left fade if we've scrolled right
    setShowLeftFade(scrollLeft > 5);
  };

  // Update scroll width when content changes
  useEffect(() => {
    const updateScrollWidth = () => {
      if (tableContainerRef.current) {
        setScrollWidth(tableContainerRef.current.scrollWidth);
        updateFadeIndicators();
      }
    };
    
    updateScrollWidth();
    window.addEventListener('resize', updateScrollWidth);
    
    // Also update after a short delay to catch dynamic content
    const timer = setTimeout(updateScrollWidth, 100);
    
    // Use ResizeObserver for more accurate detection
    const resizeObserver = new ResizeObserver(updateScrollWidth);
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateScrollWidth);
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [children]);

  return (
    <div className={`relative ${className}`}>
      {/* Top Synchronized Scrollbar */}
      <div 
        ref={topScrollRef}
        onScroll={handleTopScroll}
        className="overflow-x-auto overflow-y-hidden h-2.5 bg-[#0A0A0A] border-b border-white/5 scrollbar-thin"
        style={{ 
          scrollbarColor: '#444 #0A0A0A',
          scrollbarWidth: 'thin'
        }}
        data-testid="synced-top-scrollbar"
      >
        <div style={{ width: scrollWidth, height: 1 }} />
      </div>
      
      {/* Table Container with bottom scrollbar */}
      <div className="relative">
        <div 
          ref={tableContainerRef}
          onScroll={handleTableScroll}
          className="overflow-auto"
          style={{ 
            maxHeight,
            scrollbarColor: '#444 #121212',
            scrollbarWidth: 'thin'
          }}
          data-testid="synced-table-container"
        >
          {children}
        </div>
        
        {/* Right fade indicator */}
        {showFadeHint && showRightFade && (
          <div 
            className="absolute top-0 right-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l from-[#121212] to-transparent z-10"
            data-testid="scroll-fade-right"
          />
        )}
        
        {/* Left fade indicator */}
        {showFadeHint && showLeftFade && (
          <div 
            className="absolute top-0 left-0 bottom-0 w-8 pointer-events-none bg-gradient-to-r from-[#121212] to-transparent z-10"
            data-testid="scroll-fade-left"
          />
        )}
      </div>
    </div>
  );
}

export default SyncedScrollTable;
